import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { AlertTriangle, ArrowLeft, Bell, CalendarClock, MapPin, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { fmtDate } from "@/lib/format";

const ROLE = { PROJECT_MANAGER: "Project Manager" };
const money = (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : null);

/** Items that need attention, from the project dashboard counters (real data only). */
function attentionItems(c, base) {
  if (!c) return [];
  const rows = [
    [c.critical_issues, "critical issue", "critical issues", `${base}/issues`, true],
    [c.open_issues - (c.critical_issues || 0), "open issue", "open issues", `${base}/issues`, false],
    [c.overdue_drawings, "overdue drawing", "overdue drawings", `${base}/engineering`, true],
    [c.overdue_materials, "overdue material", "overdue materials", `${base}/materials`, true],
    [c.overdue_activities, "overdue activity", "overdue activities", `${base}/construction`, true],
    [c.open_alerts, "open alert", "open alerts", `${base}/alerts`, false],
    [c.expired_documents, "expired document", "expired documents", `${base}/documents`, true],
    [c.expiring_documents, "document expiring soon", "documents expiring soon", `${base}/documents`, false],
  ];
  return rows.filter(([n]) => Number(n) > 0).map(([n, one, many, to, hot]) => ({ n: Number(n), label: Number(n) === 1 ? one : many, to, hot }));
}

export function ProjectHeader({ project, projectId, projectRole, onBack }) {
  const [counters, setCounters] = useState(null);
  useEffect(() => {
    let live = true;
    Promise.all([
      api.get(`/projects/${projectId}/dashboard`).then(({ data }) => data?.counters || {}),
      api.get(`/projects/${projectId}/documents/summary`).then(({ data }) => ({ expired_documents: data.expired, expiring_documents: data.expiring_soon })).catch(() => ({})),
    ]).then(([a, b]) => { if (live) setCounters({ ...a, ...b }); }).catch(() => {});
    return () => { live = false; };
  }, [projectId]);

  const base = `/projects/${projectId}`;
  const end = project?.target_end_date ? dayjs(project.target_end_date) : null;
  const done = project?.status === "COMPLETED" || project?.status === "CLOSED";
  const left = end ? end.startOf("day").diff(dayjs().startOf("day"), "day") : null;
  const items = attentionItems(counters, base);

  /* project-360-own-header */
  if (typeof window !== "undefined" && /\/360\/?$/.test(window.location.pathname)) return null;
  return (
    <div className="space-y-3 border-b border-border pb-4" data-testid="project-header">
      <PageBreadcrumb crumbs={[{ label: "Projects", to: "/erp/delivery/all-projects" }, { label: project?.code || "…" }]} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <button
            data-testid="back-to-portfolio-button"
            onClick={onBack}
            aria-label="Back to portfolio"
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-mono text-xs font-semibold text-[#910029] dark:text-[#C4587B]" data-testid="project-shell-code">{project?.code || "…"}</p>
              {project?.status && <StatusPill value={project.status} />}
            </div>
            <h1 className="text-xl font-semibold text-foreground" data-testid="project-shell-name">{project?.name || "Loading project…"}</h1>
          </div>
        </div>
        {projectRole && <span className="rounded border border-border bg-secondary px-2 py-1 text-[11px] font-medium text-muted-foreground" data-testid="project-shell-role">My role: {ROLE[projectRole] || projectRole.replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase())}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground" data-testid="project-header-meta">
        {project?.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{project.location}</span>}
        {money(project?.contract_value) && <span className="flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" />Contract {money(project.contract_value)}</span>}
        {(project?.start_date || end) && (
          <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />{fmtDate(project.start_date)} → {fmtDate(project.target_end_date)}
            {!done && left !== null && (
              <span className={left < 0 ? "font-medium text-red-600 dark:text-red-400" : ""}>
                {left < 0 ? ` · ${-left} day${left === -1 ? "" : "s"} past target` : left === 0 ? " · due today" : ` · ${left} day${left === 1 ? "" : "s"} left`}
              </span>
            )}
          </span>
        )}
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="project-attention">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" />Needs attention</span>
          {items.map((it) => (
            <Link
              key={it.label}
              to={it.to}
              className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs transition-colors hover:bg-accent ${it.hot ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300" : "border-border bg-secondary text-foreground"}`}
            >
              {it.hot ? null : <Bell className="h-3 w-3 text-muted-foreground" />}<b>{it.n}</b> {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
